import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useGetChatRoomListQuery } from '~/features/chatRoom';
import {
  useDailyRewardMutation,
  useRewardStatusQuery,
  useSurveyLinkQuery,
} from '~/features/dart';
import { useModal } from '~/hooks';
import { calculateRemainingTimeUntilMidnight, GAEvent } from '~/utils';
import { GA, PAGE_ROUTE } from '~/constants';
import dart from '~/assets/img/icons/dart.svg';
import { useUserStore } from '~/store';
import useToast from '~/store/useToast';
import { FlexStack } from '../atomic';
import { DartRewardBottomSheet } from '../common';
import BottomSheetContainer from '../common/BottomSheet/BottomSheet';

const DartRewardArr = [
  { id: 'dailyBonus', dart: 30 },
  { id: 'shareFeedback', dart: 100 },
];

const RewardModalType = {
  REWARD_DAILY: 'reward_daily',
  REWARD_SURVEY: 'reward_survey',
} as const;

type RewardModalType = (typeof RewardModalType)[keyof typeof RewardModalType];

export default function DartReward() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { openModal } = useModal();
  const email = useUserStore((state) => state.email);
  const { data: chatRoomListData } = useGetChatRoomListQuery();

  const [timer, setTimer] = useState<string>('00:00:00');
  const [isOpen, setIsOpen] = useState(false);
  const [rewardModalType, setRewardModalType] = useState<RewardModalType>(
    RewardModalType.REWARD_DAILY,
  );

  const {
    data: rewardState,
    isLoading: isRewardStatusLoading,
    refetch: refetchRewardStatus,
  } = useRewardStatusQuery();
  const { data: surveyLink } = useSurveyLinkQuery();
  const { mutate: getReward } = useDailyRewardMutation();

  const isDailyRewardReady = !rewardState?.reward_daily;
  const isSurveyRewardReady =
    rewardState?.reward_survey === 'before_survey' ||
    rewardState?.reward_survey === 'before_reward';

  useEffect(() => {
    if (!rewardState?.reward_daily) return;

    const updateTimer = () => {
      const remainingTime = calculateRemainingTimeUntilMidnight();

      if (remainingTime <= 0) {
        setTimer('');
        refetchRewardStatus();
        return;
      }

      const hours = Math.floor(remainingTime / 3600);
      const minutes = Math.floor((remainingTime % 3600) / 60);
      const seconds = remainingTime % 60;

      setTimer(
        `${hours.toString().padStart(2, '0')}:${minutes
          .toString()
          .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`,
      );
    };

    updateTimer();
    const intervalId = setInterval(updateTimer, 1000);
    return () => clearInterval(intervalId);
  }, [rewardState?.reward_daily, refetchRewardStatus]);

  const handleDailyReward = async () => {
    if (!isDailyRewardReady || !email) return;

    // send GA event : CLICK_CLAIM_REWARD
    GAEvent(email, GA.ACTION.CLICK_CLAIM_REWARD, {
      reward_id: 'dailybonus',
    });

    const timezone_iana = Intl.DateTimeFormat().resolvedOptions().timeZone;

    getReward(
      { type: 'daily', timezone_iana },
      {
        onSuccess() {
          setRewardModalType(RewardModalType.REWARD_DAILY);
          setIsOpen(true);
        },
      },
    );
  };

  const handleShareFeedback = async () => {
    if (chatRoomListData.pages.length < 3) {
      openModal({
        title: 'Explore more to share!',
        content: 'Try 3 chat rooms to unlock the survey.',
        buttonName: 'Go chat',
        click: () => navigate(PAGE_ROUTE.MAIN),
      });
      return;
    }

    if (!surveyLink) {
      showToast('error', 'Survey link not found.');
      return;
    }

    if (rewardState?.reward_survey === 'before_survey') {
      window.open(surveyLink, '_blank', 'noopener,noreferrer');
    } else if (rewardState?.reward_survey === 'before_reward') {
      // try {
      GAEvent(email, GA.ACTION.CLICK_CLAIM_REWARD, {
        reward_id: 'feedback',
      });

      const timezone_iana = Intl.DateTimeFormat().resolvedOptions().timeZone;

      getReward(
        { type: 'survey', timezone_iana },
        {
          onSuccess() {
            setRewardModalType(RewardModalType.REWARD_SURVEY);
            setIsOpen(true);
          },
        },
      );
    }
  };

  return (
    <>
      <Container>
        <DartPackageItem key={`dart_package_dailyBonus`}>
          <DartCountBox
            isRewardReady={isDailyRewardReady && !isRewardStatusLoading}
          >
            <IconBox>
              <DartIcon src={dart} alt='' />
              <Dart>{DartRewardArr[0].dart}</Dart>
            </IconBox>
            <DartCount>Daily bonus</DartCount>
          </DartCountBox>
          <FlexStack direction='row'>
            {isDailyRewardReady || isRewardStatusLoading ? (
              <Button
                disabled={!isDailyRewardReady || isRewardStatusLoading}
                onClick={handleDailyReward}
              >
                Claim
              </Button>
            ) : (
              <NextIn direction='row' spacing={0}>
                <strong>Next in</strong>
                <span>{timer}</span>
              </NextIn>
            )}
          </FlexStack>
        </DartPackageItem>

        <DartPackageItem key={`dart_package_shareFeedback`}>
          <DartCountBox
            isRewardReady={isSurveyRewardReady && !isRewardStatusLoading}
          >
            <IconBox>
              <DartIcon src={dart} alt='' />
              <Dart>{DartRewardArr[1].dart}</Dart>
            </IconBox>
            <DartCount>Share feedback</DartCount>
          </DartCountBox>

          <Button
            disabled={!isSurveyRewardReady || isRewardStatusLoading}
            onClick={handleShareFeedback}
          >
            {rewardState?.reward_survey === 'before_survey'
              ? 'Go'
              : rewardState?.reward_survey === 'before_reward'
              ? 'Claim'
              : 'Received'}
          </Button>
        </DartPackageItem>
      </Container>

      <BottomSheetContainer isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <DartRewardBottomSheet
          dartCount={
            rewardModalType === RewardModalType.REWARD_DAILY ? 30 : 100
          }
          description={
            rewardModalType === RewardModalType.REWARD_DAILY
              ? 'Today Only'
              : 'No expiry'
          }
          onClose={() => setIsOpen(false)}
        />
      </BottomSheetContainer>
    </>
  );
}

export const Container = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const DartPackageItem = styled.li`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    padding-left: 16px;
    border: 1px solid ${theme.stroke.stroke_1};
    border-radius: 16px;
  `}
`;

export const IconBox = styled.div`
  position: relative;
`;

export const DartCountBox = styled.div<{ isRewardReady?: boolean }>`
  ${({ theme, isRewardReady = true }) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;

    ${!isRewardReady &&
    css`
      img {
        opacity: 0.5;
      }

      strong,
      span {
        color: ${theme.content.content_light_4};
      }
    `}
  `}
`;

export const DartIcon = styled.img`
  display: block;
  width: 24px;
  height: 22px;
`;

export const Dart = styled.span`
  ${({ theme }) => css`
    display: block;
    position: absolute;
    right: -10px;
    bottom: -5px;
    ${theme.fontStyle.text.body14_bold}
  `}
`;

export const DartCount = styled.strong`
  ${({ theme }) => css`
    ${theme.fontStyle.text.body16_medium}
  `}
`;

export const Button = styled.button`
  ${({ theme }) => css`
    display: block;
    width: 100px;
    height: 44px;
    padding: 8px;
    border-radius: 12px;
    background: ${theme.themeColor.main_light};
    ${theme.fontStyle.text.body16_bold}
    color: ${theme.content.content_dark_1};

    &:disabled {
      background: ${theme.backgroundColor.disabled};
      color: ${theme.content.content_light_5};
    }
  `}
`;

export const NextIn = styled(FlexStack)`
  ${({ theme }) => css`
    align-items: center;
    height: 44px;
    margin-right: 8px;

    strong {
      ${theme.fontStyle.text.body16_medium}
      color: ${theme.content.hierarchy1};
    }

    span {
      ${theme.fontStyle.text.body16_medium}
      width: 70px;
      color: ${theme.themeColor.main_light};
      text-align: right;
    }
  `}
`;
